import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import {  IoMdArrowRoundBack } from "react-icons/io";
import { Link } from "react-router-dom";
import { AiOutlineClockCircle, AiOutlineQuestionCircle } from "react-icons/ai";
import {
  useGetAllAssignmentQuestionQuery,
  useAssignmentsInFolderQuery,
  useGetAllFoldersQuery,
  useGetOtherAssignmentQuery,
  useUpdateFolderMutation,
} from "../../../../graphql/generated.tsx";
import Loader from "../../../../components/Loader";
import { toast } from "../../../../toast";
import { useParams } from "react-router-dom";

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const getRowAccent = (globalIndex, rowSize = 3) => {
  const rowIndex = Math.floor(globalIndex / rowSize);
  const indexInRow = globalIndex % rowSize;
  const permutations = [[0, 1, 2], [1, 2, 0], [2, 0, 1], [0, 2, 1], [2, 1, 0], [1, 0, 2]];
  const order = permutations[rowIndex % permutations.length];
  return ACCENTS[order[indexInRow] ?? 0];
};

const CalSVG = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const formatDurationLabel = (durationValue) => {
  if (durationValue === null || durationValue === undefined || durationValue === "") return "-";
  const raw = String(durationValue).trim();
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return "-";
  const [hoursPart, minutesPartRaw] = raw.split(".");
  if (minutesPartRaw?.length === 2) {
    const hours = Number.parseInt(hoursPart, 10) || 0;
    const minutes = Number.parseInt(minutesPartRaw, 10);
    if (Number.isFinite(minutes) && minutes >= 0 && minutes <= 59) {
      return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
    }
  }
  const totalMinutes = Math.round(numeric * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
};

const AssignmentCard = ({ assignment, globalIndex, folderid }) => {
  const accent = getRowAccent(globalIndex);
  const [isHovered, setIsHovered] = useState(false);
  const { data: questionsData } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId: assignment?.id || "", page: 1, limit: 1000 },
    skip: !assignment?.id,
  });
  const questionsCount = questionsData?.getAllAssignmentQuestion?.length ?? null;

  return (
    <Link
      to={`/admin/assignment-folders/${folderid}/assignments/${assignment.id}`}
      className="font-poppins"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e5e7eb",
        boxSizing: "border-box",
        boxShadow: "0 12px 30px -18px rgba(15, 23, 42, 0.2)",
        transition: "box-shadow 0.22s ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ height: 7, background: `${accent.bar}99`, width: "100%", flexShrink: 0 }} />
      <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#101828", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {assignment.name}
        </p>
        <p style={{ margin: 0, fontSize: 14, color: "#5B6475", lineHeight: 1.7, flex: 1, fontWeight: 500, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {assignment.short_description || "Take this assessment based on what you know and demonstrate your understanding."}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", padding: "4px 0", marginTop: 6 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
            <AiOutlineClockCircle size={16} color="#64748B" />
            {formatDurationLabel(assignment.duration)}
          </div>
          <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
            <AiOutlineQuestionCircle size={16} color="#64748B" />
            {questionsCount ?? "-"} questions
          </div>
          <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
            <CalSVG />
            {assignment.due_date ? new Date(assignment.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "No due date"}
          </div>
        </div>
      </div>
      <div style={{ padding: "12px 18px 16px", display: "flex", alignItems: "center", borderTop: "1px solid #F2F4F7", gap: 10, background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))" }}>
        {assignment.domain ? <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 9px", borderRadius: 8, letterSpacing: "0.03em", whiteSpace: "nowrap", background: accent.pillBg, color: accent.pillText }}>{assignment.domain.toUpperCase()}</span> : <span />}
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: isHovered ? accent.pillBgHover : "#F9FAFB", border: `1px solid ${isHovered ? accent.bar : "#EAECF0"}`, color: isHovered ? accent.bar : "#667085", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: "auto", transition: "background 0.2s ease-in-out" }}>
          <ArrowSVG />
        </div>
      </div>
    </Link>
  );
};

const Assignments = () => {
  const { folderid } = useParams();
  const [assignments, setAssignments] = useState([]);
  const [Isload, setIsload] = useState(true);
  const hasSyncedOtherAssignments = useRef(false);

  const { data: foldersData, loading: isFoldersLoading } = useGetAllFoldersQuery();
  const currentFolder = foldersData?.getAllFolders?.find(
    (folder) => folder.id === folderid || folder._id === folderid
  );
  const isOthersFolder =
    currentFolder?.name?.trim().toLowerCase() === "others";
  const shouldUseOtherAssignments = !isFoldersLoading && isOthersFolder;

  const {
    data: folderAssignmentsData,
    refetch: refetchAssignmentsInFolder,
    loading: isAssignmentsLoading,
  } = useAssignmentsInFolderQuery({
    variables: { folderId: folderid },
    skip: !folderid,
  });
  const {
    data: otherAssignmentsData,
    refetch: refetchOtherAssignments,
    loading: isOtherAssignmentsLoading,
  } = useGetOtherAssignmentQuery({
    skip: !shouldUseOtherAssignments,
  });
  const [updateFolder] = useUpdateFolderMutation();
  const isDataLoading =
    isFoldersLoading || isAssignmentsLoading || isOtherAssignmentsLoading || Isload;

     useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);

  useEffect(() => {
    hasSyncedOtherAssignments.current = false;
  }, [folderid]);

  useEffect(() => {
    if (shouldUseOtherAssignments) {
      refetchOtherAssignments();
    }
  }, [shouldUseOtherAssignments, refetchOtherAssignments]);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        refetchAssignmentsInFolder();
        if (folderAssignmentsData?.assignmentsInFolder) {
          setAssignments(folderAssignmentsData?.assignmentsInFolder);
        }
      } catch (error) {
        toast.error("Error in fetching !");
      }
    };

    fetchAssignments();
  }, [
    folderAssignmentsData,
    refetchAssignmentsInFolder,
  ]);

  useEffect(() => {
    const syncOtherAssignments = async () => {
      if (!shouldUseOtherAssignments || hasSyncedOtherAssignments.current) {
        return;
      }

      const assignmentIds = (otherAssignmentsData?.getOtherAssignment ?? [])
        .map((assignment) => assignment.id)
        .filter(Boolean);

      if (!currentFolder?.id || assignmentIds.length === 0) {
        return;
      }

      try {
        await updateFolder({
          variables: {
            id: currentFolder.id,
            updateFolderDto: {
              name: currentFolder.name,
              assignments: assignmentIds,
            },
          },
        });
        refetchAssignmentsInFolder();
        hasSyncedOtherAssignments.current = true;
      } catch (error) {
        toast.error("Error in updating !");
      }
    };

    syncOtherAssignments();
  }, [
    shouldUseOtherAssignments,
    otherAssignmentsData,
    currentFolder,
    updateFolder,
    refetchAssignmentsInFolder,
  ]);

  if (isDataLoading) {
     return (
       <AdminLayout>
         <div className="flex items-center justify-center h-screen">
          <Loader />
         </div>
       </AdminLayout>
     );
   }

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="min-h-screen p-8 bg-white">
        <div className="flex items-center justify-between mb-8 max-md:gap-2">
          <div className="flex items-center">
            <button
              onClick={() => window.history.back()}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <IoMdArrowRoundBack className="w-6 h-6" />
            </button>
            <div className="ml-2 text-2xl font-bold max-md:text-lg">
              All Assignments
            </div>
          </div>
        </div>

        {isDataLoading ? (
          <div className="flex items-center justify-center h-screen">
            <Loader />
          </div>
        ) : assignments.length === 0 ? (
          <div className="flex items-center justify-center h-screen">
            <p className="text-gray-500">No assignments found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {assignments.map((assignment, index) => (
              <AssignmentCard
                key={assignment.id}
                assignment={assignment}
                globalIndex={index}
                folderid={folderid}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Assignments;
